// =====================================================
// COMPOSANT: RomainSection — « Tableau de Romain » (RUN 6, TOUTE FIN de page)
// =====================================================
// Tableau MENSUEL auto-généré (une ligne par mois) des métriques d'acquisition
// externe de Romain, avec comparaison N-1 mois par mois. Lucile s'en sert pour
// ses points. Design system crm-4l (zinc). Config-driven : les colonnes suivent
// `romain.metrics` (source = ROMAIN_METRICS) → ajuster le format = éditer la lib.
//
// ⚠️ Un bandeau signale que le FORMAT EXACT reste à confirmer avec Desertours
// (les libellés exacts de Romain ne sont pas encore reçus de Paul).

import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { RomainCell, RomainData } from '@/lib/dashboard-stats';

/** Δ N-1 en absolu (value − prevValue), coloré. null → « — ». */
function CellDelta({ cell }: { cell: RomainCell }) {
  if (cell.prevValue == null) return <span className="text-zinc-300">—</span>;
  const delta = cell.value - cell.prevValue;
  const sign = delta > 0 ? '+' : '';
  const color = delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-zinc-400';
  return (
    <span className="tabular-nums text-zinc-500">
      {cell.prevValue.toLocaleString('fr-FR')}{' '}
      <span className={color}>
        ({sign}
        {delta.toLocaleString('fr-FR')})
      </span>
    </span>
  );
}

interface RomainSectionProps {
  romain: RomainData;
  showN1: boolean;
}

export function RomainSection({ romain, showN1 }: RomainSectionProps) {
  const { metrics, rows, hasN1, isEmpty, flags } = romain;
  const showN1Col = showN1 && hasN1;

  return (
    <section className="flex flex-col gap-6">
      {/* Titre de section */}
      <div className="flex items-baseline gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-700">
          Tableau de Romain
        </h2>
        <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
          Acquisition externe · mensuel
        </span>
        <div className="h-px flex-1 bg-zinc-200" />
      </div>

      <Card className="border border-zinc-200">
        <CardContent className="flex flex-col gap-4 px-6">
          {/* Bandeau « format à confirmer » (honnêteté nomenclature) */}
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs leading-relaxed text-amber-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Structure best-effort sur les métriques connues du cadrage. Le{' '}
              <strong className="font-semibold">format exact de Romain reste à confirmer</strong>{' '}
              avec Desertours (libellés en attente de Paul) — colonnes facilement ajustables côté
              code.
            </span>
          </div>

          {isEmpty ? (
            <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-zinc-200 text-xs text-zinc-400">
              Aucune donnée d'acquisition sur l'édition.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
                    <th className="sticky left-0 z-10 bg-white py-2 pr-4 text-left font-semibold">
                      Mois
                    </th>
                    {metrics.map((m) => (
                      <th
                        key={m.key}
                        className="px-4 py-2 text-right font-semibold"
                        title={m.description}
                      >
                        {m.label}
                        {showN1Col ? (
                          <span className="ml-1 font-normal normal-case text-zinc-400">
                            (N / N-1)
                          </span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.monthStart} className="border-t border-zinc-100">
                      <td className="sticky left-0 z-10 bg-white py-2 pr-4 text-left text-sm font-medium capitalize text-zinc-800">
                        {row.label}
                      </td>
                      {metrics.map((m) => {
                        const cell = row.cells[m.key];
                        return (
                          <td key={m.key} className="px-4 py-2 text-right align-top">
                            <div className="flex flex-col items-end">
                              <span className="text-sm font-semibold tabular-nums text-zinc-900">
                                {cell.value.toLocaleString('fr-FR')}
                              </span>
                              {showN1Col ? (
                                <span className="text-[11px]">
                                  <CellDelta cell={cell} />
                                </span>
                              ) : null}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Réserves nomenclature (repliées en note discrète) */}
          {flags.length > 0 ? (
            <ul className="flex flex-col gap-1 text-[11px] leading-relaxed text-zinc-400">
              {flags.map((f, i) => (
                <li key={i} className="flex gap-1.5">
                  <span className="text-zinc-300">·</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}
